import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormCard } from "Components/Form/FormCard";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Alert, Button, Col, Form, Input, Popconfirm, Radio, Row, Switch, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { formatMcpTs, isHttpUrl, isMcpOauthClientId, redirectUrisAreValid } from "./mcpAdminFormat";
import { McpAdminFeedback } from "./McpAdminFeedback";

export const McpClientsTab = () => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<API_McpOAuthClient[]>([]);
  const [policy, setPolicy] = useState<string>("open");
  const [form] = Form.useForm<API_McpOAuthClient>();
  const [feedback, setFeedback] = useState<string | null>(null);

  const showOk = (key: string) => {
    const msg = t(key);
    notificationSuccess(t("Success"), msg);
    setFeedback(msg);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [list, admission] = await Promise.all([
        API.getData<API_McpOAuthClient[]>(API_URL.MCP_ADMIN.getClients()),
        API.getData<API_McpAdmissionPolicy>(API_URL.MCP_ADMIN.getAdmission()),
      ]);
      setClients(list ?? []);
      setPolicy(admission?.policy || "open");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const savePolicy = async (next: string) => {
    const saved = await API.editData<API_McpAdmissionPolicy>(API_URL.MCP_ADMIN.updateAdmission(), { policy: next });
    setPolicy(saved?.policy || next);
    showOk("Admission policy saved");
  };

  const saveClient = async (values: API_McpOAuthClient) => {
    await API.newData(API_URL.MCP_ADMIN.upsertClient(), { ...values, clientType: values.clientType || "public" });
    form.resetFields();
    await fetchAll();
    showOk("MCP client saved");
  };

  const removeClient = async (id: number) => {
    await API.deleteData(API_URL.MCP_ADMIN.deleteClient(id));
    form.resetFields();
    await fetchAll();
    showOk("MCP client deleted");
  };

  const columns: ColumnsType<API_McpOAuthClient> = [
    { dataIndex: "clientId", title: t("Client ID"), ellipsis: true },
    { dataIndex: "clientName", title: t("Client name"), ellipsis: true },
    {
      dataIndex: "enabled",
      title: t("Enabled"),
      width: 110,
      render: (enabled: boolean) => (enabled ? t("Yes") : t("No")),
    },
    { dataIndex: "cimdUrl", title: t("CIMD URL"), ellipsis: true },
    {
      dataIndex: "lastChange",
      title: t("Last change"),
      width: 220,
      render: (value: string | number) => formatMcpTs(value),
    },
    {
      title: t("Actions"),
      width: 80,
      render: (_v, record) =>
        record.id != null ? (
          <Popconfirm title={t("Delete MCP client?")} onConfirm={() => removeClient(record.id)}>
            <Button type="link" size="small" aria-label={t("Delete MCP client")} onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon icon={["far", "trash-alt"]} />
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <div>
      <FormCard title={t("Admission policy")} loading={loading} className="mb-2">
        <McpAdminFeedback message={feedback} />
        <p className="mb-2">
          {t(
            `Which MCP clients (for example Cursor) may start an OAuth login. Checked at authorize. The Registrations tab is only the DCR log (cap per IP), not this allowlist.`
          )}
        </p>
        <Radio.Group value={policy} onChange={(e) => savePolicy(e.target.value)}>
          <Radio value="open">{t("Open — all MCP clients may connect")}</Radio>
          <Radio value="allowlist">{t("Allowlist — only enabled rows below")}</Radio>
        </Radio.Group>
        {policy === "open" ? (
          <Alert
            className="mt-2"
            type="info"
            showIcon
            title={t("Open: table is not a gate")}
            description={t(
              `Any client_id may connect, even without a row. Entries here are optional (documentation). Switch to allowlist only after the clients you need are enabled in the table — otherwise Cursor MCP will fail. The change applies immediately.`
            )}
          />
        ) : (
          <Alert
            className="mt-2"
            type="warning"
            showIcon
            title={t("Allowlist: only enabled rows")}
            description={t(
              `Unknown or disabled client_id values are rejected. Redirect URIs, if set, must match the authorize request. If the table is empty, no MCP client can connect (including Cursor). The change applies immediately.`
            )}
          />
        )}
      </FormCard>
      <FormCard title={t("Clients")} loading={loading}>
        <p className="mb-2">{t("One row per client_id that should pass under allowlist. Click a row to edit.")}</p>
        <Form form={form} layout="vertical" onFinish={saveClient} initialValues={{ enabled: true }}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="clientId"
                label={t("Client ID")}
                extra={t(
                  `OAuth client_id of the MCP app (for example Cursor), usually a UUID from dynamic registration — not the PAVE user id. Under allowlist this value must match the authorize request exactly.`
                )}
                rules={[
                  { required: true, message: t("Client ID is required") },
                  {
                    validator: async (_, value) => {
                      if (!value || isMcpOauthClientId(value)) {
                        return;
                      }
                      throw new Error(t("Client ID must be a UUID or http(s) CIMD URL"));
                    },
                  },
                ]}
              >
                <Input placeholder={t("xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx or https://…")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="clientName" label={t("Client name")}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item name="enabled" label={t("Enabled")} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="cimdUrl"
                label={t("CIMD URL")}
                extra={t("Only needed when the client_id is an http(s) metadata URL.")}
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value || !String(value).trim() || isHttpUrl(value)) {
                        return;
                      }
                      throw new Error(t("CIMD URL must be http(s)"));
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="redirectUris"
                label={t("Redirect URIs")}
                extra={t("Optional, comma-separated. If set, the authorize redirect_uri must be included.")}
                rules={[
                  {
                    validator: async (_, value) => {
                      if (redirectUrisAreValid(value)) {
                        return;
                      }
                      throw new Error(t("Redirect URIs must have a scheme"));
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit">
            {t("Save client")}
          </Button>
        </Form>
        <Table
          className="mt-2"
          rowKey={(row) => String(row.id ?? row.clientId)}
          columns={columns}
          dataSource={clients}
          size="small"
          pagination={false}
          locale={{
            emptyText:
              policy === "allowlist" ? t("Allowlist empty: no MCP client can connect") : t("No client rows yet"),
          }}
          onRow={(record) => ({
            onClick: () => form.setFieldsValue(record),
          })}
        />
      </FormCard>
    </div>
  );
};
